import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

const api = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 10000,
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('ldm_token');
    if (token) {
      config.headers.Authorization = token;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.data) {
      const apiError = new Error(
        error.response.data.message || 'An unexpected error occurred'
      );
      apiError.status  = error.response.status;
      apiError.data    = error.response.data;
      return Promise.reject(apiError);
    }
    return Promise.reject(error);
  }
);

export const login  = (credentials) => api.post('/auth/login', credentials);
export const logout = ()            => api.post('/auth/logout');

/**
 * Creates a new user. Requires ROLE_ADMIN JWT in localStorage.
 *
 * @param {Object} payload - { username, email, password, role, enabled }
 * @returns {Promise<AxiosResponse<UserResponse>>} the created user (password excluded)
 */
export const createUser = (payload) => api.post('/users', payload);

/**
 * Fetches a paginated, searchable, filterable list of users.
 *
 * @param {Object} params
 * @param {string}  [params.search]   - substring matched against username / email
 * @param {string}  [params.role]     - ADMIN | USER | MANAGER
 * @param {boolean} [params.enabled]  - true = active, false = inactive
 * @param {number}  [params.page]     - 0-based page index (default 0)
 * @param {number}  [params.size]     - page size (default 10)
 * @param {string}  [params.sortBy]   - field name (default 'id')
 * @param {string}  [params.sortDir]  - 'asc' | 'desc' (default 'asc')
 * @returns {Promise<AxiosResponse<PageResponse<UserResponse>>>}
 */
export const getUsers   = (params = {}) => api.get('/users', { params });
export const updateUser = (id, payload) => api.patch(`/users/${id}`, payload);
export const deleteUser = (id)          => api.delete(`/users/${id}`);

/**
 * Downloads the sample Excel import template as a binary blob.
 *
 * @returns {Promise<AxiosResponse<Blob>>}
 */
export const downloadSampleTemplate = () =>
  api.get('/template/sample/download', { responseType: 'blob' });

/**
 * Uploads an Excel file containing lead data.
 * FormData must contain:
 *   - `file`    → the .xlsx / .xls file blob
 *   - `purpose` → string campaign tag applied to every saved record
 *
 * @param {FormData} formData
 * @returns {Promise<AxiosResponse<LeadUploadResponse>>}
 */
export const uploadLeads = (formData) =>
  api.post('/leads/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  });

/**
 * Accepts the failedRecords array returned by uploadLeads and downloads
 * them as a correctable .xlsx file with an extra "Error Reason" column.
 *
 * @param {Array} failedRecords  - the failedRecords array from LeadUploadResponse
 * @returns {Promise<AxiosResponse<Blob>>}
 */
export const downloadFailedLeads = (failedRecords) =>
  api.post('/leads/export/failed', failedRecords, { responseType: 'blob' });

/**
 * Fetches a paginated, searchable, filterable list of contacts.
 *
 * @param {Object} params
 * @param {string}  [params.search]      - searches firstName, lastName, email, companyName
 * @param {string}  [params.purpose]     - partial match on purpose
 * @param {string}  [params.createdBy]   - partial match on createdBy username
 * @param {string}  [params.companyName] - exact match on companyName (drill-down from Companies)
 * @param {string}  [params.dateFrom]    - ISO date string yyyy-MM-dd
 * @param {string}  [params.dateTo]      - ISO date string yyyy-MM-dd
 * @param {number}  [params.page]        - 0-based page index (default 0)
 * @param {number}  [params.size]        - page size (default 10)
 * @param {string}  [params.sort]        - e.g. 'createdAt,desc'
 * @returns {Promise<AxiosResponse<PageResponse<LeadResponse>>>}
 */
export const getContacts       = (params = {}) => api.get('/contacts', { params });
export const getContactById    = (id)          => api.get(`/contacts/${id}`);
export const updateContact     = (id, payload) => api.patch(`/contacts/${id}`, payload);
export const getInteractions   = (leadId)      => api.get(`/contacts/${leadId}/interactions`);
export const logInteraction    = (leadId, payload) => api.post(`/contacts/${leadId}/interactions`, payload);

/**
 * Fetches a paginated, searchable, filterable list of companies aggregated from leads.
 *
 * @param {Object} params
 * @param {string}  [params.search]   - searches companyName, industry, location
 * @param {string}  [params.industry] - partial match on industry
 * @param {string}  [params.location] - partial match on location
 * @param {number}  [params.page]     - 0-based page index (default 0)
 * @param {number}  [params.size]     - page size (default 10)
 * @param {string}  [params.sort]     - e.g. 'companyName,asc'
 * @returns {Promise<AxiosResponse<PageResponse<CompanyResponse>>>}
 */
export const getCompanies   = (params = {}) => api.get('/companies', { params });

/**
 * Partially updates company-level fields for every contact under that company.
 * If companyName is included in the payload the company is renamed across all contacts.
 *
 * @param {string} companyName - email domain of the company (will be URL-encoded, e.g. acme.com)
 * @param {Object} payload     - fields to update (omit unchanged fields)
 * @returns {Promise<AxiosResponse<void>>}
 */
export const updateCompany  = (companyName, payload) =>
  api.patch(`/companies/${encodeURIComponent(companyName)}`, payload);

/**
 * Fetches a paginated list of leads with DNC or BOUNCE status.
 *
 * @param {Object} params
 * @param {string}  params.status  - 'DNC' or 'BOUNCE'
 * @param {string}  [params.search] - substring matched against email, name, company
 * @param {number}  [params.page]   - 0-based page index (default 0)
 * @param {number}  [params.size]   - page size (default 20)
 * @param {string}  [params.sort]   - e.g. 'updatedAt,desc'
 * @returns {Promise<AxiosResponse<PageResponse<LeadResponse>>>}
 */
export const getDncBounceList = (params = {}) => api.get('/dnc-bounce', { params });

/**
 * Marks email addresses or domains as DNC or BOUNCE.
 *
 * @param {Object} payload
 * @param {string}   payload.status     - 'DNC' or 'BOUNCE'
 * @param {string}   payload.entryType  - 'EMAIL' or 'DOMAIN'
 * @param {string[]} payload.values     - list of email addresses or domain names
 * @returns {Promise<AxiosResponse<void>>}
 */
export const markDncBounce = (payload) => api.post('/dnc-bounce/mark', payload);

/**
 * Sets a lead's status back to ACTIVE (removes from DNC/Bounce list).
 *
 * @param {string} leadId - UUID of the lead
 * @returns {Promise<AxiosResponse<void>>}
 */
export const unmarkLead = (leadId) => api.delete(`/dnc-bounce/${leadId}`);

/**
 * Returns all domains on the DNC domain list.
 *
 * @returns {Promise<AxiosResponse<DncDomainResponse[]>>}
 */
export const getDncDomains = () => api.get('/dnc-bounce/domains');

/**
 * Removes a domain rule from the DNC domain list.
 *
 * @param {string} id - UUID of the DncDomain record
 * @returns {Promise<AxiosResponse<void>>}
 */
export const removeDncDomain = (id) => api.delete(`/dnc-bounce/domains/${id}`);

/**
 * Downloads contact records as an Excel file, optionally filtered. Admin only.
 *
 * @param {Object} params - filter parameters (same as getContacts)
 * @returns {Promise<AxiosResponse<Blob>>}
 */
export const downloadContacts = (params = {}) =>
  api.get('/contacts/export', { params, responseType: 'blob' });

/**
 * Downloads the DNC or Bounce list as an Excel file. Admin only.
 *
 * @param {'DNC'|'BOUNCE'} status
 * @returns {Promise<AxiosResponse<Blob>>}
 */
export const downloadDncBounce = (status) =>
  api.get('/dnc-bounce/export', { params: { status }, responseType: 'blob' });

/**
 * Returns all dashboard statistics for admin/manager users.
 * Includes scalar counts, chart data (by industry/location/purpose), and recent activity.
 *
 * @returns {Promise<AxiosResponse<DashboardResponse>>}
 */
export const getDashboard = () => api.get('/dashboard');

export default api;
